import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ToggleLeft, ToggleRight, Save, Info } from "lucide-react";

const WINDOWS = [
  { value: "monthly", label: "شهرياً" },
  { value: "bimonthly", label: "كل شهرين" },
  { value: "opportunity", label: "طوال مدة الفرصة" },
  { value: "until_graduation", label: "حتى تخرّج الطالب" },
];

export default function AdminPlacementSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [associations, setAssociations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        api.get("/admin/placement-config"),
        api.get("/users?role=association"),
      ]);
      setConfig(c.data.config);
      setAssociations(u.data.users || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (fields) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/placement-config", fields);
      setConfig(data.config);
      toast.success("تم الحفظ");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setQuotaFor = (id, val) => {
    const q = { ...(config.quotas || {}) };
    if (val === "" || val == null) delete q[id]; else q[id] = Number(val);
    patch({ quotas: q });
  };

  if (loading || !config) return <DashboardLayout><div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div data-testid="admin-placement-settings">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحة النظام</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">إعدادات التسكين المباشر</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            تحكّم في نصاب التسكين المباشر للجمعيات، الفترة الزمنية، وحالة الميزة (مفعّلة / مغلقة). عند الإغلاق يظهر لكل جمعية إشعار يوضّح أن التسكين يتم عبر تقديم الطلاب على الفرص المنشورة.
          </p>
        </div>

        {/* Master toggle */}
        <div className={`mb-6 p-4 rounded-2xl border ${config.enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="master-toggle">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-heading font-black text-slate-900 text-lg">
                {config.enabled ? "الميزة مفعّلة" : "الميزة مغلقة"}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {config.enabled
                  ? "الجمعيات تستطيع اختيار الطلاب مباشرة ضمن النصاب المحدد."
                  : "الجمعيات لن تتمكن من التسكين المباشر — التسكين يتم عبر التقديم على الفرص فقط."}
              </div>
            </div>
            <button
              disabled={saving}
              onClick={() => patch({ enabled: !config.enabled })}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white disabled:opacity-60 ${config.enabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              data-testid="toggle-btn"
            >
              {config.enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
              {config.enabled ? "إغلاق الميزة" : "تفعيل الميزة"}
            </button>
          </div>
        </div>

        {/* Defaults */}
        <div className="mb-6 grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow">
            <label className="block text-sm font-bold text-slate-800 mb-2">النصاب الافتراضي للجمعية</label>
            <input type="number" min={0} max={200}
              defaultValue={config.default_quota}
              onBlur={(e) => patch({ default_quota: Number(e.target.value) })}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-lg font-heading font-black text-teal-800 arabic-numerals"
              data-testid="default-quota"
            />
            <p className="mt-2 text-xs text-slate-500">هذا العدد يُطبَّق على كل الجمعيات ما لم يتم تخصيص نصاب مختلف لكل جمعية أدناه.</p>
          </div>
          <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow">
            <label className="block text-sm font-bold text-slate-800 mb-2">فترة تجديد النصاب</label>
            <select value={config.window_type} onChange={(e) => patch({ window_type: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="window-type">
              {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
            <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1"><Info className="h-3 w-3" /> يتم تجديد النصاب تلقائياً حسب هذه الفترة.</p>
          </div>
        </div>

        {/* Per-association quotas */}
        <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="assoc-quotas">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-black text-slate-900">تخصيص نصاب لكل جمعية</h2>
            <span className="text-xs text-slate-500 arabic-numerals">{associations.length} جمعية</span>
          </div>
          {associations.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">لا توجد جمعيات مسجّلة.</div>
          ) : (
            <div className="divide-y divide-cream-100">
              {associations.map((a) => (
                <div key={a.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`assoc-row-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900">{a.organization_name || a.name}</div>
                    <div className="text-xs text-slate-500">{a.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">النصاب:</span>
                    <input type="number" min={0} max={200}
                      defaultValue={config.quotas?.[a.id] ?? ""}
                      placeholder={String(config.default_quota)}
                      onBlur={(e) => setQuotaFor(a.id, e.target.value)}
                      className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm text-center arabic-numerals"
                      data-testid={`quota-input-${a.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
